import type { Sanitizer } from '@angular/core';

import { pipe } from 'rxjs';

import type { I18nService } from '@core/i18n-legacy';
import {
  Blueprint,
  DataInterval,
  DatasetBase,
  HeaderRow,
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewData,
  ReportDashboardComponentViewMode,
  ReportDashboardMetricOptions,
  ReportsBarChartOptions,
  ReportsEChartsOption,
  ReportsTranslations,
  SummaryData,
  TableData,
  ValueRow,
  VisualizationMapperOperator,
  VisualizationType,
} from '@core/reports-types';
import {
  datasetValueFormatterOptions,
  getComponentVisualizationType,
  NET_CHANGE_NET_COLUMN,
} from '@core/reports-util';
import { pipeCompose } from '@core/utils-legacy';

import { buildNetChangeBars } from './strategies/composables/net-change';
import { addIntervalXAxis, addValueYAxis } from './strategies/composables/trend';
import {
  addYAxisCategoryTruncate,
  createYAxisCategoryFactory,
} from './strategies/config-factories/y-axis';
import { dataZoom } from './strategies/datazoom';
import { getXAxisNameLabel } from './strategies/get-x-axis-name-label';
import { netChangeTooltipContextFactory } from './strategies/net-change/tooltip';
import {
  formatNet,
  formatNetChangeValue,
  netChangeSign,
  readNetChangeRow,
} from './strategies/net-change/values';
import { filterByMetricKind, fromBlueprints } from './strategies/operators';
import { formatDateWithInterval } from './strategies/trend/date-format';

/**
 * Both value axes count occurrences in both directions - the negative metric is
 * only flipped for layout - so the axis is labelled with absolute values.
 */
const absoluteAxisLabel = (dataset: DatasetBase, i18n: I18nService) => ({
  formatter: (value: number) => formatNetChangeValue(Math.abs(value), dataset, i18n),
});

/**
 * Diverging columns over time: the positive metric grows up from the zero
 * baseline, the negative one down.
 */
const columnChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  clampEndToToday: boolean,
): ReportsEChartsOption => {
  const { dataset: chartDataset, series } = buildNetChangeBars(dataset, 'vertical');
  const interval = <DataInterval>component.settings?.interval ?? DataInterval.Monthly;

  const opts = () => ({
    graphic: [getXAxisNameLabel(metric.xAxis)],
    dataset: chartDataset,
    series,
    tooltip: <any>{
      show: true,
      trigger: <const>'axis',
      axisPointer: { type: <const>'none' },
      formatter: (params: any) =>
        netChangeTooltipContextFactory(
          dataset,
          translations,
          params,
          formatDateWithInterval(
            params[0]?.axisValueLabel,
            component.dateFilter,
            interval,
            clampEndToToday,
          ).primary,
        ),
    },
  });

  return pipeCompose(
    opts,
    addValueYAxis(datasetValueFormatterOptions(dataset), translations.i18n),
    o => ({
      ...o,
      yAxis: { ...o.yAxis, axisLabel: absoluteAxisLabel(dataset, translations.i18n) },
    }),
    addIntervalXAxis(dataset, interval),
  ) satisfies ReportsEChartsOption;
};

/**
 * Back-to-back horizontal bars per dimension value: the negative metric extends
 * left of zero, the positive one right.
 */
const barChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
): ReportsBarChartOptions<'value', 'category'> => {
  const { dataset: chartDataset, series } = buildNetChangeBars(dataset, 'horizontal');

  const yAxis = pipeCompose(createYAxisCategoryFactory(dataset), addYAxisCategoryTruncate);

  return {
    graphic: [getXAxisNameLabel(metric.xAxis)],
    dataset: chartDataset,
    series,
    dataZoom: dataZoom(component, dataset.getRawData()),
    xAxis: {
      type: <const>'value',
      minInterval: 1,
      boundaryGap: ['5%', '5%'],
      axisLabel: { show: true, ...absoluteAxisLabel(dataset, translations.i18n) },
    },
    yAxis,
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: { type: 'none' },
      formatter: (params: any) =>
        <any>(
          netChangeTooltipContextFactory(
            dataset,
            translations,
            params,
            dataset.getCategoryNameById(params[0]?.name),
          )
        ),
    },
  };
};

/**
 * Same layout as the generic table, with the backend computed net column marked
 * so its sign gets colour-coded and stays out of the details drilldown.
 */
const netChangeTable = (i18n: I18nService): Blueprint<TableData> => ({
  match: component =>
    component.kind === ReportDashboardComponentKind.NetChange &&
    getComponentVisualizationType(component) === VisualizationType.Table,
  factory: ({ dataset }) => {
    const raw = dataset.getRawData();
    const headerRow = <HeaderRow>raw.slice(0, 1).pop();
    const valueRows = <Array<ValueRow<number>>>raw.slice(1);

    return {
      vis: <TableData>(<unknown>{
        dimensions: headerRow,
        values: valueRows.map(([dimension, ...values], rowIndex) => {
          const { net } = readNetChangeRow(dataset, rowIndex);
          return [
            dimension,
            ...values.map((value, index) => {
              const isNetColumn = index + 1 === NET_CHANGE_NET_COLUMN;
              return {
                value,
                formatted: isNetColumn
                  ? formatNet(net, dataset, i18n)
                  : formatNetChangeValue(value, dataset, i18n),
                ...(isNetColumn && { sign: netChangeSign(net), clickable: false }),
              };
            }),
          ];
        }),
      }),
      dataset,
    };
  },
});

/** One tile per row showing the signed, colour-coded net change. */
const netChangeSummary = (i18n: I18nService): Blueprint<SummaryData> => ({
  match: {
    kind: ReportDashboardComponentKind.NetChange,
    viewMode: ReportDashboardComponentViewMode.Summary,
  },
  factory: ({ dataset }) => {
    const raw = dataset.getRawData();

    return {
      vis: <SummaryData>{
        dimensions: raw[0],
        values: raw.slice(1),
        items: raw.slice(1).map((row, rowIndex) => {
          const { net } = readNetChangeRow(dataset, rowIndex);
          return {
            clickable: false,
            dimensions: { primary: row[0] },
            header: { primary: row[0]?.name },
            value: formatNet(net, dataset, i18n),
            rawValue: net,
            sign: netChangeSign(net),
          };
        }),
      },
      dataset,
    };
  },
});

export const netChangeBlueprints = (
  sanitizer: Sanitizer,
  translations: ReportsTranslations,
  clampEndToToday = false,
): Array<Blueprint<any>> => [
  netChangeTable(translations.i18n),
  netChangeSummary(translations.i18n),
  {
    match: {
      kind: ReportDashboardComponentKind.NetChange,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Column,
    },
    factory: ({ component, dataset, metric }) => ({
      vis: columnChart(component, dataset, metric, translations, clampEndToToday),
      dataset,
    }),
  },
  {
    match: {
      kind: ReportDashboardComponentKind.NetChange,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Bar,
    },
    factory: ({ component, dataset, metric }) => ({
      vis: barChart(component, dataset, metric, translations),
      dataset,
    }),
  },
];

export const netChange = (
  sanitizer: Sanitizer,
  translations: ReportsTranslations,
  clampEndToToday = false,
): VisualizationMapperOperator =>
  pipe(
    filterByMetricKind(ReportDashboardComponentKind.NetChange),
    fromBlueprints(netChangeBlueprints(sanitizer, translations, clampEndToToday)),
  );
